import { useMemo, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useTheme } from "next-themes";
import {
  ArrowLeft, Copy, Lock, MessageCircle, Moon, Plus, Send, ShieldCheck, Sun, Trash2, UserPlus, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LangToggle } from "@/components/ui/LangToggle";
import {
  createContactVault, hasContactVault, lockContactVault, openContactVault, saveContactVault, type LocalContact,
} from "../lib/contactVault";
import {
  buildInviteText, getMessengerWebUrl, getSmsShareUrl, getTelegramWebUrl, getWhatsAppWebShareUrl, tryNativeRoomShare,
} from "../lib/roomInvite";

function ThemeButton() {
  const { theme, setTheme } = useTheme();
  const isOnyx = theme === "onyx";
  return (
    <Button type="button" variant="outline" size="sm" onClick={() => setTheme(isOnyx ? "aurora" : "onyx")}
      className="h-10 rounded-xl border-border/70 bg-card/60 px-3 text-muted-foreground hover:bg-accent hover:text-foreground">
      {isOnyx ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
      <span>Changer de thème</span>
    </Button>
  );
}

function RoomHeader({ onBack }: { onBack: () => void }) {
  return (
    <header className="sticky top-0 z-20 w-full border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex min-h-16 w-full max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        {/* Desktop and mobile controls are intentionally isolated.
            The mobile header hides the ROOM branding and keeps only the
            theme/language controls plus a larger corner back button. */}
        <div className="hidden items-center gap-3 sm:flex">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-primary/30 bg-primary/10">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="text-base font-semibold tracking-wide">ROOM</div>
            <div className="hidden text-[11px] text-muted-foreground sm:block">Contacts privés</div>
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-1.5 sm:w-auto sm:gap-2">
          <ThemeButton />
          <LangToggle />
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onBack}
            aria-label="Retour à ROOM"
            title="Retour à ROOM"
            className="h-12 w-12 rounded-2xl p-0 sm:h-10 sm:w-auto sm:rounded-xl sm:px-4"
          >
            <ArrowLeft className="h-5 w-5 sm:mr-2 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Retour à ROOM</span>
          </Button>
        </div>
      </div>
    </header>
  );
}

function PageShell({ children, onBack }: { children: ReactNode; onBack: () => void }) {
  return (
    <div className="min-h-[100dvh] bg-background text-foreground">
      <RoomHeader onBack={onBack} />
      <main className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-8 sm:px-6 sm:py-12">{children}</main>
    </div>
  );
}

export function ContactsPage() {
  const [, setLocation] = useLocation();
  const [contacts, setContacts] = useState<LocalContact[]>([]);
  const [password, setPassword] = useState("");
  const [panicPassword, setPanicPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [pseudo, setPseudo] = useState("");
  const [numero, setNumero] = useState("");
  const [ready, setReady] = useState(false);
  const [setup, setSetup] = useState(!hasContactVault());
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [shareContact, setShareContact] = useState<LocalContact | null>(null);

  const roomReturn = useMemo(() => {
    if (typeof window === "undefined") return "/";
    const params = new URLSearchParams(window.location.search);
    const roomId = params.get("room");
    const hash = window.location.hash || "";
    return roomId ? `/room/${encodeURIComponent(roomId)}${hash}` : "/";
  }, []);

  const roomId = useMemo(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("room") || "";
  }, []);

  const goBack = () => {
    if (typeof window !== "undefined") {
      window.location.assign(roomReturn);
      return;
    }
    setLocation(roomReturn);
  };

  async function setupVault() {
    try {
      setError("");
      if (password !== passwordConfirm) {
        setError("Les deux mots de passe ne correspondent pas.");
        return;
      }
      await createContactVault(password, panicPassword);
      setSetup(false); setReady(true); setContacts([]);
      setPassword(""); setPasswordConfirm(""); setPanicPassword("");
    } catch (e) { setError(e instanceof Error ? e.message : "Impossible de créer le coffre."); }
  }

  async function unlock() {
    try {
      setError("");
      const result = await openContactVault(password);
      setPassword("");
      if (result.panic) { setContacts([]); setReady(false); setSetup(true); return; }
      setContacts(result.contacts); setReady(true);
    } catch (e) { setError(e instanceof Error ? e.message : "Impossible d'ouvrir le coffre."); }
  }

  async function addContact() {
    const p = pseudo.trim(), n = numero.trim();
    if (!p || !n) { setError("Renseigne un pseudo et un numéro."); return; }
    try {
      setError("");
      const next = [...contacts, { id: crypto.randomUUID(), pseudo: p, numero: n }];
      await saveContactVault(next); setContacts(next); setPseudo(""); setNumero("");
    } catch (e) { setError(e instanceof Error ? e.message : "Impossible d'enregistrer le contact."); }
  }

  async function removeContact(id: string) {
    try {
      setError("");
      const next = contacts.filter((c) => c.id !== id);
      await saveContactVault(next); setContacts(next);
    } catch (e) { setError(e instanceof Error ? e.message : "Impossible de supprimer le contact."); }
  }

  const openInvite = async (contact: LocalContact) => {
    setError("");
    const native = await tryNativeRoomShare(roomId, inviteMessage);
    if (native === "shared" || native === "cancelled") return;
    setShareContact(contact);
  };

  const invitePayload = shareContact ? buildInviteText(roomId, inviteMessage) : null;

  const openExternalShare = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setShareContact(null);
  };

  const copyThenOpenWebMessenger = async () => {
    if (!invitePayload) return;
    try {
      await navigator.clipboard.writeText(invitePayload.text);
    } catch {
      // Clipboard may be unavailable; the invitation remains visible in the dialog.
    }
    openExternalShare(getMessengerWebUrl());
  };

  const copyThenOpenWebTelegram = async () => {
    if (!invitePayload) return;
    try {
      await navigator.clipboard.writeText(invitePayload.text);
    } catch {
      // Clipboard may be unavailable; the invitation remains visible in the dialog.
    }
    openExternalShare(getTelegramWebUrl());
  };

  const copyInvite = async () => {
    if (!invitePayload) return;
    try {
      await navigator.clipboard.writeText(invitePayload.url);
      setShareContact(null);
    } catch { setError("Impossible de copier le lien."); }
  };

  const filteredContacts = contacts.filter((contact) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return contact.pseudo.toLowerCase().includes(q) || contact.numero.includes(q);
  });

  if (setup) {
    return (
      <PageShell onBack={goBack}>
        <div className="mb-7 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10 shadow-lg shadow-primary/10"><ShieldCheck className="h-7 w-7 text-primary" /></div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Contacts ROOM</h1>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">Votre carnet reste uniquement sur cette machine et est chiffré.</p>
        </div>
        <section className="w-full rounded-2xl border border-border/70 bg-card/70 p-5 shadow-xl shadow-black/10 backdrop-blur-xl sm:p-7">
          <div className="mb-6"><h2 className="text-lg font-semibold">Créer le coffre</h2><p className="mt-1 text-sm text-muted-foreground">Définissez un mot de passe principal et un mot de passe PANIC.</p></div>
          <div className="space-y-4">
            <label className="block space-y-2"><span className="text-sm font-medium">Mot de passe</span><input type="password" autoComplete="new-password" placeholder="Votre mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 w-full rounded-xl border border-border bg-background/70 px-4 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
            <label className="block space-y-2"><span className="text-sm font-medium">Confirmation</span><input type="password" autoComplete="new-password" placeholder="Confirmez le mot de passe" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} className="h-12 w-full rounded-xl border border-border bg-background/70 px-4 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
            <label className="block space-y-2"><span className="text-sm font-medium">Mot de passe PANIC</span><input type="password" autoComplete="new-password" placeholder="Un mot de passe PANIC différent" value={panicPassword} onChange={(e) => setPanicPassword(e.target.value)} className="h-12 w-full rounded-xl border border-border bg-background/70 px-4 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm leading-5 text-muted-foreground"><div className="flex gap-3"><Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>Le mot de passe PANIC efface définitivement le coffre de contacts.</span></div></div>
            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
            <Button type="button" onClick={() => void setupVault()} className="h-12 w-full rounded-xl text-sm font-semibold">Créer le coffre</Button>
          </div>
        </section>
      </PageShell>
    );
  }

  if (!ready) {
    return (
      <PageShell onBack={goBack}>
        <div className="mb-7 text-center"><div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10"><Lock className="h-7 w-7 text-primary" /></div><h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Contacts ROOM</h1><p className="mt-2 text-sm text-muted-foreground">Déverrouillez votre carnet local.</p></div>
        <section className="w-full max-w-md rounded-2xl border border-border/70 bg-card/70 p-5 shadow-xl shadow-black/10 backdrop-blur-xl sm:p-7">
          <label className="block space-y-2"><span className="text-sm font-medium">Mot de passe</span><input type="password" autoComplete="current-password" placeholder="Votre mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void unlock()} className="h-12 w-full rounded-xl border border-border bg-background/70 px-4 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
          {error && <p className="mt-3 text-sm text-destructive" role="alert">{error}</p>}
          <Button type="button" onClick={() => void unlock()} className="mt-5 h-12 w-full rounded-xl">Déverrouiller</Button>
          <p className="mt-4 text-center text-xs leading-5 text-muted-foreground">Le mot de passe PANIC peut être utilisé à la place du mot de passe normal pour supprimer le coffre.</p>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell onBack={goBack}>
      <div className="mb-7 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10"><UserPlus className="h-7 w-7 text-primary" /></div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Contacts ROOM</h1>
        <p className="mt-2 text-sm text-muted-foreground">{contacts.length} contact{contacts.length > 1 ? "s" : ""} enregistré{contacts.length > 1 ? "s" : ""} sur cette machine.</p>
      </div>

      <section className="w-full rounded-2xl border border-border/70 bg-card/70 p-4 shadow-xl shadow-black/10 backdrop-blur-xl sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input placeholder="Pseudo" value={pseudo} onChange={(e) => setPseudo(e.target.value)} className="h-12 min-w-0 flex-1 rounded-xl border border-border bg-background/70 px-4 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 sm:h-11" />
          <input inputMode="tel" placeholder="Numéro" value={numero} onChange={(e) => setNumero(e.target.value)} className="h-12 min-w-0 flex-1 rounded-xl border border-border bg-background/70 px-4 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 sm:h-11" />
          <Button type="button" onClick={() => void addContact()} className="h-11 rounded-xl px-5"><Plus className="mr-2 h-4 w-4" />Ajouter</Button>
        </div>

        <div className="my-5 h-px bg-border/60" />

        <label className="mb-4 block">
          <span className="mb-2 block text-xs font-medium text-muted-foreground">Message personnel d'invitation</span>
          <input value={inviteMessage} onChange={(e) => setInviteMessage(e.target.value)} placeholder="Ex. Rejoins-moi dans ma room ROOM 👋" className="h-11 w-full rounded-xl border border-border bg-background/70 px-4 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
        </label>

        <input placeholder="Rechercher un contact…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-4 h-11 w-full rounded-xl border border-border bg-background/70 px-4 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
        {error && <p className="mb-4 text-sm text-destructive" role="alert">{error}</p>}

        <div className="space-y-2">
          {filteredContacts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/70 py-10 text-center"><UserPlus className="mx-auto mb-3 h-7 w-7 text-muted-foreground/60" /><p className="text-sm text-muted-foreground">Aucun contact.</p></div>
          ) : (
            filteredContacts.map((contact) => (
              <article key={contact.id} className="flex flex-col gap-3 rounded-xl border border-border/60 bg-background/40 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0"><strong className="block truncate text-sm font-semibold">{contact.pseudo}</strong><span className="mt-1 block text-sm text-muted-foreground">{contact.numero}</span></div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button type="button" variant="ghost" size="sm" className="h-10 rounded-lg" onClick={() => void openInvite(contact)} title={`Inviter ${contact.pseudo}`}>
                    <Send className="mr-2 h-4 w-4" />Inviter
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="h-10 w-10 rounded-lg text-destructive hover:text-destructive" title="Supprimer" onClick={() => void removeContact(contact.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </article>
            ))
          )}
        </div>

        <div className="mt-5 flex flex-col gap-3 border-t border-border/60 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5 text-primary" />Contacts chiffrés et conservés uniquement sur cette machine.</div>
          <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => { lockContactVault(); setReady(false); setPassword(""); }}>
            <Lock className="mr-2 h-4 w-4" />Verrouiller
          </Button>
        </div>
      </section>

      <Dialog open={!!shareContact} onOpenChange={(open) => !open && setShareContact(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Partager l'invitation</DialogTitle>
            <DialogDescription>{shareContact ? `Envoyer le lien ROOM à ${shareContact.pseudo}.` : "Choisissez un moyen d'envoi."}</DialogDescription>
          </DialogHeader>
          {shareContact && invitePayload && (
            <div className="space-y-3 pt-2">
              <div className="rounded-xl border border-border/60 bg-muted/30 p-3 text-sm break-all">{invitePayload.url}</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button type="button" onClick={() => openExternalShare(getWhatsAppWebShareUrl(shareContact.numero, invitePayload.text))} className="h-11 rounded-xl">WhatsApp Web</Button>
                <Button type="button" variant="outline" onClick={() => void copyThenOpenWebTelegram()} className="h-11 rounded-xl">Telegram Web</Button>
                <Button type="button" variant="outline" onClick={() => void copyThenOpenWebMessenger()} className="h-11 rounded-xl"><MessageCircle className="mr-2 h-4 w-4" />Messenger Web</Button>
                <Button type="button" variant="outline" onClick={() => openExternalShare(getSmsShareUrl(shareContact.numero, invitePayload.text))} className="h-11 rounded-xl">SMS</Button>
                <Button type="button" variant="outline" onClick={() => void copyInvite()} className="h-11 rounded-xl"><Copy className="mr-2 h-4 w-4" />Copier</Button>
              </div>
              <Button type="button" variant="ghost" onClick={() => setShareContact(null)} className="w-full"><X className="mr-2 h-4 w-4" />Fermer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

export default ContactsPage;
